import { CommunicationCard } from './CommunicationCard';
import type { ClientCommunicationView } from '../types';

interface CommunicationGridProps {
  communications: ClientCommunicationView[];
  onCommunicationClick?: (communication: ClientCommunicationView) => void;
  onDelete?: (communication: ClientCommunicationView) => void;
  emptyMessage?: string;
  showDeleteButton?: boolean;
  isLoading?: boolean;
}

export function CommunicationGrid({
  communications,
  onCommunicationClick,
  onDelete,
  emptyMessage = 'No communications yet',
  showDeleteButton = false,
  isLoading = false,
}: CommunicationGridProps) {
  // Loading skeleton
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="aspect-square bg-secondary-200 rounded-lg" />
            <div className="mt-2 space-y-2">
              <div className="h-4 bg-secondary-200 rounded w-3/4" />
              <div className="h-3 bg-secondary-200 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Empty state
  if (communications.length === 0) {
    return (
      <div className="text-center py-12 bg-secondary-50 rounded-lg">
        <svg
          className="w-12 h-12 mx-auto text-secondary-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
          />
        </svg>
        <p className="mt-2 text-secondary-600">{emptyMessage}</p>
        <p className="mt-1 text-sm text-secondary-500">
          Upload screenshots, emails, or PDFs of client conversations
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
      {communications.map((communication) => (
        <CommunicationCard
          key={communication.id}
          communication={communication}
          onClick={() => onCommunicationClick?.(communication)}
          onDelete={() => onDelete?.(communication)}
          showDeleteButton={showDeleteButton}
        />
      ))}
    </div>
  );
}
